import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { Mail, Lock, LogIn } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      const next = location.state?.from || "/";
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center bg-brand text-brand-mint font-sans relative overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 h-[520px] w-[1000px] rounded-full bg-brand-mint/18 blur-[140px]" />
        <div className="absolute bottom-[-15%] right-[-10%] h-[420px] w-[560px] rounded-full bg-brand-mint/15 blur-[120px]" />
      </div>

      <div className="relative w-full max-w-md mx-auto px-6">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_launch-agents/artifacts/09567i82_launchhub.png"
            alt="LaunchHub"
            className="h-20 w-20 mx-auto mb-4 rounded-full object-cover ring-2 ring-brand-mint/30 shadow-2xl shadow-brand-deeper/40"
          />
          <h1 className="text-3xl font-bold tracking-tight text-brand-mint">
            Welcome back
          </h1>
          <p className="mt-2 text-sm text-brand-mint/70">
            Sign in to continue to LaunchHub
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-brand-deep/50 backdrop-blur-xl border border-brand-mint/20 rounded-2xl p-6 md:p-8 space-y-5 shadow-2xl shadow-brand-deeper/40"
        >
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-brand-mint/65 font-medium">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-mint/50" />
              <input
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full rounded-lg border border-brand-mint/20 bg-brand-deeper/60 pl-10 pr-3 py-2.5 text-sm text-brand-mint placeholder:text-brand-mint/40 focus:outline-none focus:ring-2 focus:ring-brand-mint/40 focus:border-brand-mint/60"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-brand-mint/65 font-medium">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-mint/50" />
              <input
                type="password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-lg border border-brand-mint/20 bg-brand-deeper/60 pl-10 pr-3 py-2.5 text-sm text-brand-mint placeholder:text-brand-mint/40 focus:outline-none focus:ring-2 focus:ring-brand-mint/40 focus:border-brand-mint/60"
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-red-200 bg-red-500/15 border border-red-400/30 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-brand-mint hover:bg-brand-mintBright text-brand font-semibold transition-colors disabled:opacity-50"
          >
            <LogIn className="h-4 w-4" />
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <div className="text-center text-sm text-brand-mint/70">
            New here?{" "}
            <Link
              to="/register"
              data-testid="login-register-link"
              className="text-brand-mintBright font-semibold hover:underline"
            >
              Request access
            </Link>
          </div>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/admin"
            data-testid="login-admin-link"
            className="text-xs text-brand-mint/55 hover:text-brand-mint/80 tracking-wider uppercase"
          >
            Admin
          </Link>
        </div>
      </div>
    </div>
  );
}
